import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';
import {IQuestionData, IQuestionType} from '../models';
import {ITisAnswerValue} from '../models/project/project-data';

export type QuestionFormAnswer = string | Date | number | boolean;

/** Integer answers map to a 32-bit int on the backend. */
export const INT32_MIN = -2_147_483_648;
export const INT32_MAX = 2_147_483_647;

/** Matches a plain (optionally negative, optionally decimal) number, e.g. "-12" or "12.5". */
export const NUMERIC_INPUT_PATTERN = /^-?\d+(\.\d+)?$/;
export type ProjectVersionBumpKind = 'major' | 'minor';


export function createIntegerMask(min: number = INT32_MIN, max: number = INT32_MAX): Record<string, unknown> {
    return {
        alias: 'integer',
        groupSeparator: '.',
        digits: 0,
        placeholder: '',
        allowMinus: min < 0,
        min,
        max,
        SetMaxOnOverflow: true
    };
}

export function createFinancialMask(min: number = INT32_MIN, max: number = INT32_MAX): Record<string, unknown> {
    return {
        alias: 'integer',
        prefix: '€ ',
        groupSeparator: '.',
        digits: 0,
        placeholder: '',
        allowMinus: min < 0,
        min,
        max,
        SetMaxOnOverflow: true
    };
}


export function int32RangeValidator(min: number = INT32_MIN, max: number = INT32_MAX): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const raw = control.value;
        if (raw === null || raw === undefined || raw === '') {
            return null;
        }

        const normalized = `${raw}`.replace(/\./g, '').trim();
        if (!/^-?\d+$/.test(normalized)) {
            return {int32Range: {min, max}};
        }

        const value = Number(normalized);
        if (!Number.isInteger(value) || value < min || value > max) {
            return {int32Range: {min, max}};
        }

        return null;
    };
}


export class QuestionAnswerUtil {
    public static toAnswerValue(question: IQuestionData, answer: QuestionFormAnswer): ITisAnswerValue {
        if (question.type === IQuestionType.DATE) {
            let transformedValue = answer as Date;

            if (typeof transformedValue === 'string' && transformedValue === 'Invalid date') {
                transformedValue = null;
            }

            return { date: transformedValue, attachments: [] };
        } else if (question.type === IQuestionType.TEXT) {
            return { text: answer as string, attachments: [] };
        } else if (question.type === IQuestionType.INTEGER || question.type === IQuestionType.CALCULATION ||
            question.type === IQuestionType.FINANCIAL) {
            return { integer: answer as number, attachments: [] };
        } else if (question.type === IQuestionType.BOOL || question.type === IQuestionType.CHECKBOX) {
            return { bool: answer as boolean, attachments: [] };
        } else if (question.type === IQuestionType.PERCENTAGE) {
            return { percentage: answer as number, attachments: [] };
        } else if (question.type === IQuestionType.MULTIPLECHOICE) {
            return { answerId: answer as number, attachments: [] };
        } else if (question.type === IQuestionType.DEPARTMENT) {
            return { departmentId: answer as number, attachments: [] };
        } else {
            throw new Error(`Unrecognized question type ${question.type}`);
        }
    }

    public static isAnswered(question: IQuestionData, answer: QuestionFormAnswer | number[]): boolean {
        if (Array.isArray(answer)) {
            return answer.length > 0;
        }

        switch (question.type) {
            case IQuestionType.TEXT:
                return answer !== null && answer !== undefined && answer !== '';
            case IQuestionType.INTEGER:
            case IQuestionType.CALCULATION:
            case IQuestionType.PERCENTAGE:
            case IQuestionType.FINANCIAL:
                return answer !== null && answer !== undefined && !isNaN(answer as number);
            default:
                return answer !== null && answer !== undefined;
        }
    }

    public static toFormAnswer(question: IQuestionData, restAnswer: ITisAnswerValue): QuestionFormAnswer {
        if (restAnswer === null) {
            return null;
        } else if (question.type === IQuestionType.DATE) {
            return restAnswer.date;
        } else if (question.type === IQuestionType.TEXT) {
            return restAnswer.text;
        } else if (question.type === IQuestionType.INTEGER || question.type === IQuestionType.CALCULATION ||
            question.type === IQuestionType.FINANCIAL) {
            return restAnswer.integer;
        } else if (question.type === IQuestionType.BOOL || question.type === IQuestionType.CHECKBOX) {
            return restAnswer.bool;
        } else if (question.type === IQuestionType.PERCENTAGE) {
            return restAnswer.percentage;
        } else if (question.type === IQuestionType.MULTIPLECHOICE) {
            return restAnswer.answerId;
        } else if (question.type === IQuestionType.DEPARTMENT) {
            return restAnswer.departmentId;
        } else {
            throw new Error(`Unrecognized question type ${question.type}`);
        }
    }
}
